import { computed, inject, Injectable, signal } from '@angular/core'
import { OAuthErrorEvent, OAuthInfoEvent, OAuthService, OAuthSuccessEvent } from 'angular-oauth2-oidc'
import { Router } from '@angular/router'
import { authConfig } from '../../../environments/auth.config'
import { BehaviorSubject, debounceTime, firstValueFrom, of, share, shareReplay, Subject, switchMap } from 'rxjs'
import { toSignal } from '@angular/core/rxjs-interop'

export interface OAuthClaims {
  given_name: string
  family_name: string
  nickname: string
  name: string
  picture: string
  updated_at: string
  email: string
  email_verified: true
  iss: string
  sub: string
  aud: string
  iat: number
  exp: number
  at_hash: string
  nonce: string
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  router = inject(Router)
  oauthService = inject(OAuthService)

  readonly doneSubject = new Subject<void>()
  readonly done = this.doneSubject.pipe(shareReplay(1))

  tokenExpiring = new BehaviorSubject<boolean>(false)

  tokenExpiring$ = this.tokenExpiring.asObservable().pipe(
    debounceTime(10000),
    switchMap((value) => {
      if (value === true) {
        const accessTokenExpiration = this.oauthService.getAccessTokenExpiration()
        if (!accessTokenExpiration) {
          return of(undefined)
        }
        return of(new Date(accessTokenExpiration))
      }
      return of(undefined)
    }),
    share(),
  )

  readonly expirationDateTime = toSignal(this.tokenExpiring$, {
    initialValue: undefined,
  })

  readonly validAccessToken = signal<boolean | undefined>(undefined)

  readonly isAuthenticated = computed(() => {
    return this.validAccessToken()
  })

  readonly #currentIdentityClaims = signal<OAuthClaims | undefined>(undefined)

  readonly userInfo = computed(() => this.#currentIdentityClaims())

  constructor() {
    this.oauthService.events.subscribe((event) => {
      if (event instanceof OAuthErrorEvent) {
        console.error(event)
      } else if (event instanceof OAuthSuccessEvent) {
        if (this.oauthService.hasValidAccessToken()) {
          this.tokenExpiring.next(false)
          this.validAccessToken.set(true)
          this.updateUserInfo()
          this.doneSubject.next()
        }
      } else if (event instanceof OAuthInfoEvent) {
        if (
          event.type === 'token_expires' ||
          event.type === 'token_refresh_error' ||
          event.type === 'silent_refresh_error'
        ) {
          console.warn(event)
          this.tokenExpiring.next(false)
        }
      }
    })

    this.oauthService.configure(authConfig)
    this.oauthService.setupAutomaticSilentRefresh()
    this.oauthService.loadDiscoveryDocumentAndTryLogin().then(() => {
      if (this.oauthService.hasValidAccessToken()) {
        this.validAccessToken.set(true)
        console.log('User is already logged in')
      }
      this.doneSubject.next()
    })
  }

  login() {
    console.log('Logging in...')
    this.oauthService.initCodeFlow()
  }

  async isLoggedIn(): Promise<boolean> {
    await firstValueFrom(this.done)
    return this.oauthService.hasValidAccessToken()
  }

  private updateUserInfo() {
    this.#currentIdentityClaims.set(this.oauthService.getIdentityClaims() as OAuthClaims)
  }

  logout() {
    this.oauthService.logOut(false)
    console.log('Logged Out')
  }

  // Debug methods
  getAccessToken(): string | null {
    return this.oauthService.getAccessToken()
  }

  debugTokenInfo() {
    console.log('Has valid access token:', this.oauthService.hasValidAccessToken())
    console.log('Access token:', this.oauthService.getAccessToken())
    console.log('Access token expires at:', new Date(this.oauthService.getAccessTokenExpiration()))
    console.log('Scopes:', this.oauthService.getGrantedScopes())
  }
}
